import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {InputNumberProps} from 'rsuite/esm/InputNumber/InputNumber';
import {UseFormSetValue} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {IUserOutput} from 'entities/user';
import useBreakpoint from 'shared/lib/hooks/useBreakpoint';

import {adjustIntervalMarkers, IIntervalMarker} from 'shared/ui/Slider/adjustIntervalMarkers';

import Slider from 'shared/ui/Slider/Slider';

interface ICheckingIntervalSliderProps extends InputNumberProps<string | number> {
    name: string;
    setValue: UseFormSetValue<any>;
    minimalValue: number;
    defaultValue?: number;
    disabled?: boolean;
    user: IUserOutput | null;
}

const INTERVALS = {
    P1: 5,
    P2: 30,
    P3: 60,
    P4: 1440,
};

const POSITIONS = {
    START: 0,
    P2: 30,
    P3: 50,
    END: 100,
};

const snapBySegment = (minutes: number, minimalValue: number): number => {
    if (minutes <= INTERVALS.P2) {
        return Math.max(Math.round(minutes), minimalValue);
    }
    if (minutes <= INTERVALS.P3) {
        return Math.round(minutes / 10) * 10;
    }
    return Math.round(minutes / 60) * 60;
};

const minutesToPercent = (minutes: number, minimalValue: number): number => {
    const clamped = Math.min(Math.max(minutes, minimalValue), INTERVALS.P4);

    if (clamped <= INTERVALS.P2) {
        const t = (clamped - minimalValue) / (INTERVALS.P2 - minimalValue);
        return POSITIONS.START + t * (POSITIONS.P2 - POSITIONS.START);
    }
    if (clamped <= INTERVALS.P3) {
        const t = (clamped - INTERVALS.P2) / (INTERVALS.P3 - INTERVALS.P2);
        return POSITIONS.P2 + t * (POSITIONS.P3 - POSITIONS.P2);
    }
    const t = (clamped - INTERVALS.P3) / (INTERVALS.P4 - INTERVALS.P3);
    return POSITIONS.P3 + t * (POSITIONS.END - POSITIONS.P3);
};

const percentToMinutes = (percent: number, minimalValue: number): number => {
    const p = Math.min(Math.max(percent, POSITIONS.START), POSITIONS.END);

    if (p <= POSITIONS.P2) {
        const t = (p - POSITIONS.START) / (POSITIONS.P2 - POSITIONS.START);
        return minimalValue + t * (INTERVALS.P2 - minimalValue);
    }
    if (p <= POSITIONS.P3) {
        const t = (p - POSITIONS.P2) / (POSITIONS.P3 - POSITIONS.P2);
        return INTERVALS.P2 + t * (INTERVALS.P3 - INTERVALS.P2);
    }
    const t = (p - POSITIONS.P3) / (POSITIONS.END - POSITIONS.P3);
    return INTERVALS.P3 + t * (INTERVALS.P4 - INTERVALS.P3);
};

const formatLabel = (minutes: number): string => {
    if (minutes < 60) return `${minutes} ${t`м`}`;
    return minutes % 60 === 0 ? `${minutes / 60} ${t`ч`}` : `${(minutes / 60).toFixed(1)} ${t`ч`}`;
};

const CheckingIntervalSlider: React.FC<ICheckingIntervalSliderProps> = ({
    name,
    minimalValue,
    defaultValue = minimalValue,
    setValue,
    user,
    disabled = false,
}) => {
    const [minutesValue, setMinutesValue] = useState(defaultValue);
    const breakpoint = useBreakpoint();
    const hasPaidTariff = Boolean(user?.tariff && typeof user.tariff !== 'string' && user.tariff.price > 0);

    const progressPct = useMemo(
        () => minutesToPercent(minutesValue, minimalValue),
        [minutesValue, minimalValue],
    );

    const fillPct = useMemo(() => {
        const extraPct = minutesValue > minimalValue && minutesValue <= INTERVALS.P2 ? 5 : 0;
        return Math.min(progressPct + extraPct, 100);
    }, [progressPct, minutesValue, minimalValue]);

    const markers = useMemo(() => {
        const markersList: IIntervalMarker[] = [
            {position: hasPaidTariff ? 5 : 1, label: `5 ${t`м`}`, minutes: 5},
            {position: 30, label: `30 ${t`м`}`, minutes: 30},
            {position: 50, label: `1 ${t`ч`}`, minutes: 60},
            {position: 70, label: `12 ${t`ч`}`, minutes: 720},
            {position: 95, label: `24 ${t`ч`}`, minutes: 1440},
        ];
        if (hasPaidTariff) markersList.unshift({position: 1, label: `1 ${t`м`}`, minutes: 1});
        return adjustIntervalMarkers(markersList, breakpoint === 'mini').map((marker) => ({
            key: marker.position,
            position: marker.position,
            label: marker.label,
            overlapped: marker.position <= fillPct,
        }));
    }, [hasPaidTariff, breakpoint, fillPct]);

    useEffect(() => {
        setValue(name, minutesValue, {shouldDirty: true});
    }, [minutesValue, name, setValue]);

    const onOffset = useCallback((offset: number, width: number) => {
        const rawMinutes = percentToMinutes((offset / width) * 100, minimalValue);
        const newValue = snapBySegment(rawMinutes, minimalValue);
        setMinutesValue(Math.min(Math.max(newValue, minimalValue), INTERVALS.P4));
    }, [minimalValue]);

    return (
        <Slider
            name={name}
            value={minutesValue}
            min={minimalValue}
            max={INTERVALS.P4}
            progressPct={progressPct}
            fillPct={fillPct}
            markers={markers}
            thumbLabel={formatLabel(minutesValue)}
            disabled={disabled}
            onOffset={onOffset}
        />
    );
};

export default CheckingIntervalSlider;
